import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSignedUrl } from 'modules/FileManagerV2/api/services';
import { fetchFiles } from 'services/fileManager/fileManagerServices';
import { getLocalStorageItem } from 'utils/localStorageUtils';
import { queryKeys } from 'hooks/config/queryClient';
import { buildFileListParams } from './buildFileListParams';

const SIGNED_URL_BATCH_SIZE = 5;

/**
 * Computes the correct page limit from user context preferences.
 * Extracted as a module-level function so it can be used both in the
 * useState initializer (sync, first render) and in the getLimit callback
 * (keeps them in sync to avoid a second API call on mount).
 */
function computePageLimit(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext) {
  if (isGlobalFileManager && userContext?.firmDetails?.pagination_preference?.file_manager?.self)
    return userContext.firmDetails.pagination_preference.file_manager.self;
  if (isCaseFileManager && userContext?.firmDetails?.pagination_preference?.case?.file_manager)
    return userContext.firmDetails.pagination_preference.case.file_manager;
  if (isLeadFileManager && userContext?.firmDetails?.pagination_preference?.leads?.file_manager)
    return userContext.firmDetails.pagination_preference.leads.file_manager;
  if (isClientFileManager && userContext?.firmDetails?.pagination_preference?.client_portals?.filemanager?.self)
    return userContext.firmDetails.pagination_preference.client_portals.filemanager.self;
  return 10;
}

const processSingleItem = async (item) => {
  const processedItem = { ...item };
  processedItem.case_name = item?.case_description?.case_name || item?.case_name || '';

  const userDetails = item?.created_by_details;
  if (userDetails) {
    const firstName = userDetails?.first_name || '';
    const lastName = userDetails?.last_name || '';
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || 'Unknown User';
    processedItem.user_full_name = fullName;
  } else {
    processedItem.user_full_name = '';
  }

  const filePath = item?.file_path;
  if (filePath && filePath.startsWith('public/')) {
    try {
      const url = await getSignedUrl(item);
      processedItem.url = url;
    } catch (error) {
      console.error(error);
      processedItem.url = null;
    }
  }
  return processedItem;
};

/**
 * Process raw API response: add signed URLs and flattened fields for sorting.
 * Processes signed URLs in batches to avoid overwhelming the backend.
 */
async function processFileListResponse(arr) {
  if (!Array.isArray(arr)) return [];
  const result = [];
  for (let i = 0; i < arr.length; i += SIGNED_URL_BATCH_SIZE) {
    const batch = arr.slice(i, i + SIGNED_URL_BATCH_SIZE);
    const processed = await Promise.all(batch.map((item) => processSingleItem(item)));
    result.push(...processed);
  }
  return result;
}

function applyPageLimit(requestParams, currentView, pageLimit, userContext, isGlobalFileManager) {
  const obj = { ...requestParams };
  if (currentView === 'grid' || getLocalStorageItem('viewFile')) {
    obj.pageLimit = 100;
  } else {
    obj.pageLimit =
      isGlobalFileManager && userContext?.firmDetails?.pagination_preference?.file_manager?.self
        ? userContext.firmDetails.pagination_preference.file_manager.self
        : pageLimit;
  }
  return obj;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useFileListQuery({
  isGlobalFileManager,
  isCaseFileManager,
  isLeadFileManager,
  isClientFileManager,
  isFirmDocuments,
  userContext,
  fileManagerFilters = {},
  caseDetails,
  currentView = 'list',
  query = '',
  pageLimit: initialPageLimit,
}) {
  // ── State ──────────────────────────────────────────────────────────────────

  const [pageLimit, setPageLimitState] = useState(
    () => initialPageLimit ?? computePageLimit(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext)
  );
  const [paginationToken, setPaginationToken] = useState(undefined);
  const [previousToken, setPreviousToken] = useState(undefined);
  const [requestPaginationToken, setRequestPaginationToken] = useState('');
  // Captures the filterSignature that was active when requestPaginationToken was set.
  // Used to derive effectiveRequestToken synchronously - see below.
  const [tokenFilterSig, setTokenFilterSig] = useState('');
  const [overrideFolderId, setOverrideFolderId] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────

  const prevFilterSignatureRef = useRef('');
  const isMountedRef = useRef(true);
  const paginationTokenRef = useRef(undefined);
  const previousTokenRef = useRef(undefined);

  // ── External hooks ─────────────────────────────────────────────────────────

  const queryClient = useQueryClient();

  // ── Derived context values ─────────────────────────────────────────────────

  const clientCaseList = userContext?.clientCaseList;
  const clientSelectedCase = userContext?.clientSelectedCase;
  const paginationPref = userContext?.firmDetails?.pagination_preference;

  // ── Stable callbacks ───────────────────────────────────────────────────────

  const setPageLimit = useCallback((value) => {
    setRequestPaginationToken('');
    setTokenFilterSig('');
    setPageLimitState(value);
  }, []);

  const fetchAndProcessFileList = useCallback(async (params, excludeFromLoading = false) => {
    const response = await fetchFiles(params, excludeFromLoading);
    const arr = response?.data?.data ?? [];
    const data = await processFileListResponse(arr);

    return {
      data,
      pagination_token: response?.data?.pagination_token,
      previous_token: response?.data?.previous_token,
    };
  }, []);

  const getLimit = useCallback(
    () => computePageLimit(isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, userContext),
    [isGlobalFileManager, isCaseFileManager, isLeadFileManager, isClientFileManager, paginationPref]
  );

  // ── Memos (in dependency order) ────────────────────────────────────────────

  // Only include the parts of userContext that determine *which* files are fetched.
  // Pagination preferences (firmDetails.pagination_preference) must NOT be included here
  // because saving a new page-limit preference updates userContext and would incorrectly
  // trigger a token reset, disabling the Next button after an items-per-page change.
  const filterSignature = useMemo(
    () =>
      JSON.stringify({
        isGlobalFileManager,
        isCaseFileManager,
        isLeadFileManager,
        isClientFileManager,
        isFirmDocuments,
        fileManagerFilters,
        caseDetails,
        clientCaseList: userContext?.clientCaseList,
        clientSelectedCase: userContext?.clientSelectedCase,
        query,
        overrideFolderId: overrideFolderId ?? undefined,
        currentView,
      }),
    [
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      fileManagerFilters,
      caseDetails,
      userContext?.clientCaseList,
      userContext?.clientSelectedCase,
      query,
      overrideFolderId,
      currentView,
    ]
  );

  const effectiveRequestToken = tokenFilterSig === filterSignature ? requestPaginationToken : '';

  const requestParams = useMemo(
    () =>
      buildFileListParams({
        isGlobalFileManager,
        isCaseFileManager,
        isLeadFileManager,
        isClientFileManager,
        isFirmDocuments,
        fileManagerFilters,
        caseDetails,
        userContext,
        query,
        overrideFolderId: overrideFolderId ?? undefined,
        pagination_token: effectiveRequestToken,
      }),
    [
      isGlobalFileManager,
      isCaseFileManager,
      isLeadFileManager,
      isClientFileManager,
      isFirmDocuments,
      fileManagerFilters,
      caseDetails,
      clientCaseList,
      clientSelectedCase,
      query,
      overrideFolderId,
      effectiveRequestToken,
    ]
  );

  const paramsWithLimit = useMemo(
    () => applyPageLimit(requestParams, currentView, pageLimit, userContext, isGlobalFileManager),
    [requestParams, currentView, pageLimit, paginationPref, isGlobalFileManager]
  );

  // ── Query setup ────────────────────────────────────────────────────────────

  // Match fetchFiles() in fileManagerServices: request_type is derived from route (global vs firm_documents etc.).
  const requestType = isFirmDocuments
    ? 'firm_documents'
    : isCaseFileManager
    ? 'case'
    : isClientFileManager || isLeadFileManager
    ? 'client'
    : 'global';

  const queryKey = queryKeys.fileManager.list({
    ...paramsWithLimit,
    view: currentView,
    requestType,
  });

  const queryFn = useCallback(() => {
    return fetchAndProcessFileList(paramsWithLimit, false);
  }, [fetchAndProcessFileList, paramsWithLimit]);

  const { data, refetch, isLoading, isFetching } = useQuery({
    queryKey,
    queryFn,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
    retry: (failureCount, error) => {
      if (error?.response?.status >= 400 && error?.response?.status < 500) return false;
      return failureCount < 2;
    },
  });

  // ── Effects ────────────────────────────────────────────────────────────────

  // Track mounted state to guard against state updates after unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset pagination tokens whenever the active filters change.
  useEffect(() => {
    if (prevFilterSignatureRef.current !== '' && prevFilterSignatureRef.current !== filterSignature) {
      if (isMountedRef.current) {
        paginationTokenRef.current = undefined;
        previousTokenRef.current = undefined;
        setPaginationToken(undefined);
        setPreviousToken(undefined);
        setRequestPaginationToken('');
        setTokenFilterSig('');
      }
    }
    prevFilterSignatureRef.current = filterSignature;
  }, [filterSignature]);

  // Keep pageLimit in sync with user preferences from context.
  useEffect(() => {
    const limit = getLimit();
    // Only update when the resolved limit is both defined and different from current state.
    // Calling setPageLimit with the same value still resets requestPaginationToken and
    // re-triggers the query, so the equality guard is important.
    if (limit != null && limit !== pageLimit && isMountedRef.current) setPageLimit(limit);
  }, [getLimit, pageLimit, setPageLimit]);

  // Sync pagination tokens from the latest query response into state and refs.
  useEffect(() => {
    if (!data || !isMountedRef.current) return;
    const nextPagination = data.pagination_token;
    const prevPagination = data.previous_token;
    paginationTokenRef.current = nextPagination;
    previousTokenRef.current = prevPagination;
    setPaginationToken(nextPagination);
    setPreviousToken(prevPagination);
    if (overrideFolderId != null) setOverrideFolderId(null);
  }, [data, overrideFolderId]);

  // ── Action callbacks ───────────────────────────────────────────────────────

  const refetchWithFolder = useCallback((folderId) => {
    setOverrideFolderId(folderId ?? null);
  }, []);

  const getFiles = useCallback(
    (obj, view, limit, excludeFromLoading = false) => {
      if (excludeFromLoading && obj) {
        const silentParams = applyPageLimit(obj, view ?? currentView, limit ?? pageLimit, userContext, isGlobalFileManager);
        const silentKey = queryKeys.fileManager.list({ ...silentParams, view: view ?? currentView, requestType });
        fetchAndProcessFileList(silentParams, true)
          .then((result) => {
            if (!isMountedRef.current) return;
            paginationTokenRef.current = result.pagination_token;
            previousTokenRef.current = result.previous_token;
            setPaginationToken(result.pagination_token);
            setPreviousToken(result.previous_token);
            queryClient.setQueryData(silentKey, {
              data: result.data,
              pagination_token: result.pagination_token,
              previous_token: result.previous_token,
            });
          })
          .catch(console.error);
        return;
      }
      refetch();
    },
    [currentView, pageLimit, userContext, isGlobalFileManager, refetch, queryClient, fetchAndProcessFileList]
  );

  const onNextPageClick = useCallback(() => {
    const token = paginationTokenRef.current ?? paginationToken ?? '';
    setRequestPaginationToken(token);
    setTokenFilterSig(filterSignature);
  }, [paginationToken, filterSignature]);

  const onPrevPageClick = useCallback(() => {
    const token = previousTokenRef.current ?? previousToken ?? '';
    setRequestPaginationToken(token);
    setTokenFilterSig(filterSignature);
  }, [previousToken, filterSignature]);

  const files = data?.data ?? [];

  return {
    files,
    getFiles,
    paginationToken,
    previousToken,
    listRequestPaginationToken: effectiveRequestToken,
    pageLimit,
    setPageLimit,
    onNextPageClick,
    onPrevPageClick,
    refetch,
    refetchWithFolder,
    isLoading,
    isFetching,
  };
}

export default useFileListQuery;
